import { BarChart3, Database, Download, FileSpreadsheet, Search, TrendingUp } from 'lucide-react'
import { useMemo } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { EnhancedDataTable } from '../shared/EnhancedDataTable'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { getColumnValue } from './column-mappings'
import { parseSemrushCsv, rowsToObjects } from './utils'

function SeoKeywordsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs

  // Handle both array and object result formats
  const resultData = Array.isArray(result) ? result[0] : result

  // Get query context from structured result
  const keyword = resultData?.query?.keyword || ''
  const action = resultData?.query?.action || ''
  const database = resultData?.query?.database || ''
  const _recordCount = resultData?.metadata?.recordCount || 0
  const executedAt = resultData?.metadata?.executedAt || new Date().toISOString()
  const csvData = resultData?.csvData

  // Export functions
  const exportToCsv = () => {
    if (!csvData || typeof csvData !== 'string') return

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `keywords-${keyword.replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const exportToXlsx = async () => {
    if (!csvData || typeof csvData !== 'string') return

    try {
      // Dynamically import xlsx to avoid loading it unless needed
      const XLSX = await import('xlsx')
      const { headers, rows } = parseSemrushCsv(csvData)

      // Create worksheet data with headers
      const worksheetData = [headers, ...rows]
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Keywords')

      // Export to file
      XLSX.writeFile(workbook, `keywords-${keyword.replace(/[^a-z0-9]/gi, '-')}-${Date.now()}.xlsx`)
    } catch (error) {
      console.error('Failed to export to XLSX:', error)
      alert('Failed to export to XLSX. Please try CSV export instead.')
    }
  }

  // Context menu actions
  const contextActions: ContextAction[] = csvData
    ? [
        {
          id: 'export-csv',
          label: 'Export CSV',
          icon: <Download className="h-3 w-3" />,
          onClick: exportToCsv,
        },
        {
          id: 'export-xlsx',
          label: 'Export XLSX',
          icon: <FileSpreadsheet className="h-3 w-3" />,
          onClick: exportToXlsx,
        },
      ]
    : []

  // Helper functions for formatting (moved outside to avoid recreation)
  const formatNumber = (value: string | undefined): string => {
    if (!value) return '-'
    const cleanValue = value.replace(/[,\s]/g, '')
    const num = Number(cleanValue)
    return Number.isFinite(num) ? num.toLocaleString() : '-'
  }

  const formatCurrency = (value: string | undefined): string => {
    if (!value) return '-'
    const num = Number(value)
    return Number.isFinite(num) ? `$${num.toFixed(2)}` : '-'
  }

  const formatPercentage = (value: string | undefined): string => {
    if (!value) return '-'
    return `${value}%`
  }

  // Memoize expensive CSV processing to avoid re-computation on every render
  const formattedRows = useMemo(() => {
    if (!csvData || typeof csvData !== 'string') return []

    const { headers, rows } = parseSemrushCsv(csvData)
    if (headers.length === 0 || rows.length === 0) return []

    const objs = rowsToObjects(headers, rows)
    return objs.map((row) => {
      // Use unified column mapping to get values
      const volume = getColumnValue(row, 'searchVolume')
      const difficulty = getColumnValue(row, 'keywordDifficulty')
      const cpc = getColumnValue(row, 'costPerClick')
      const traffic = getColumnValue(row, 'trafficPercent')
      const competition = getColumnValue(row, 'competition')
      const opportunityScore = getColumnValue(row, 'opportunityScore')
      const searchIntent = getColumnValue(row, 'searchIntent')
      const domain = getColumnValue(row, 'domain')
      const url = getColumnValue(row, 'url')
      const position = getColumnValue(row, 'position')
      const keyword = getColumnValue(row, 'keyword')

      // Build result object, only including columns that have actual values
      // This ensures columns that don't exist for an action won't appear in the table
      const result: Record<string, string> = {}

      // List of cryptic column codes to exclude (we'll use formatted versions instead)
      const crypticCodes = [
        'ph',
        'nq',
        'kd',
        'cp',
        'co',
        'in',
        'po',
        'pp',
        'pd',
        'ur',
        'dn',
        'tr',
        'tc',
        'nr',
        'td',
        'rr',
        'fk',
      ]

      // Include original row data, but exclude cryptic codes (we'll add formatted versions)
      Object.keys(row).forEach((key) => {
        if (
          row[key] !== undefined &&
          row[key] !== '' &&
          !crypticCodes.includes(key.toLowerCase())
        ) {
          result[key] = row[key]
        }
      })

      // Add formatted/standardized values only if they exist (these override any cryptic codes)
      if (keyword) result.keyword = keyword
      if (volume) result.volume = formatNumber(volume)
      if (difficulty) result.difficulty = formatPercentage(difficulty)
      if (cpc) result.cpc = formatCurrency(cpc)
      if (traffic) result.traffic = formatNumber(traffic)
      if (domain) result.domain = domain
      if (url) result.url = url
      if (position) result.position = position
      if (competition && competition !== '') result.competition = competition
      if (opportunityScore && opportunityScore !== '') result.opportunityScore = opportunityScore
      if (searchIntent && searchIntent !== '') result.searchIntent = searchIntent

      return result
    })
  }, [csvData])

  // Render optimized component when we have data
  if (formattedRows.length > 0) {
    return (
      <ContextMenuWrapper actions={contextActions}>
        <div
          className="rounded-3xl border border-gray-200/50 bg-gradient-to-br from-white/90 to-blue-50/80 text-black shadow-xl backdrop-blur-[12px]"
          style={{ pointerEvents: 'none' }}
        >
          <div className="px-6 py-4">
            <div className="flex items-start gap-4">
              <div className="mt-1 flex-shrink-0">
                <div className="rounded-full bg-gradient-to-br from-blue-100 to-blue-200 p-3 shadow-sm">
                  <Search className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-3 flex items-center gap-3">
                  <div className="rounded-md bg-gradient-to-r from-blue-500 to-indigo-600 p-1">
                    <Database className="h-4 w-4 text-white" />
                  </div>
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-base font-bold text-gray-900 text-transparent">
                    SEO Research - Keywords
                  </div>
                  {formattedRows.length > 0 && (
                    <div className="ml-auto flex items-center gap-2 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                      <TrendingUp className="h-3 w-3" />
                      {formattedRows.length} results
                    </div>
                  )}
                </div>

                {/* Query Context */}
                <div className="mb-4 rounded-xl border border-blue-100 bg-white/60 p-4 backdrop-blur-sm">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-blue-100 p-1">
                        <Search className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Target Keyword</span>
                        <div className="text-sm font-semibold text-gray-900">{keyword}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-green-100 p-1">
                        <BarChart3 className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Action Type</span>
                        <div className="text-sm font-semibold text-gray-900">{action}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-purple-100 p-1">
                        <Database className="h-3 w-3 text-purple-600" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Database</span>
                        <div className="text-sm font-semibold text-gray-900">
                          {database.toUpperCase()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-orange-100 p-1">
                        <TrendingUp className="h-3 w-3 text-orange-600" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500">Executed</span>
                        <div className="text-sm font-semibold text-gray-900">
                          {new Date(executedAt).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ pointerEvents: 'auto' }}>
                  <EnhancedDataTable
                    data={formattedRows}
                    title={`Keyword Data (${formattedRows.length} keywords)`}
                    maxRows={50}
                    priorityColumns={['keyword', 'volume', 'difficulty', 'cpc']}
                    colorCodedColumns={{
                      difficulty: 'difficulty',
                      volume: 'volume',
                      cpc: 'currency',
                      traffic: 'percentage',
                      opportunityScore: 'percentage',
                      competition: 'text',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ContextMenuWrapper>
    )
  }

  // Fallback display with just context
  return (
    <ContextMenuWrapper actions={contextActions}>
      <div
        className="rounded-3xl border border-gray-200/50 bg-gradient-to-br from-white/90 to-red-50/80 text-black shadow-xl backdrop-blur-[12px]"
        style={{ pointerEvents: 'none' }}
      >
        <div className="px-6 py-4">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex-shrink-0">
              <div className="rounded-full bg-gradient-to-br from-red-100 to-red-200 p-3 shadow-sm">
                <Search className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-3 flex items-center gap-3">
                <div className="rounded-md bg-gradient-to-r from-red-500 to-pink-600 p-1">
                  <Database className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-base font-bold text-gray-900 text-transparent">
                  SEO Research - Keywords
                </div>
                <div className="ml-auto flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                  <Search className="h-3 w-3" />
                  No data
                </div>
              </div>

              {/* Query Context */}
              <div className="mb-4 rounded-xl border border-red-100 bg-white/60 p-4 backdrop-blur-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-red-100 p-1">
                      <Search className="h-3 w-3 text-red-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Target Keyword</span>
                      <div className="text-sm font-semibold text-gray-900">{keyword || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-orange-100 p-1">
                      <BarChart3 className="h-3 w-3 text-orange-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Action Type</span>
                      <div className="text-sm font-semibold text-gray-900">{action || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-purple-100 p-1">
                      <Database className="h-3 w-3 text-purple-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Database</span>
                      <div className="text-sm font-semibold text-gray-900">
                        {database.toUpperCase() || 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="rounded-md bg-gray-100 p-1">
                      <TrendingUp className="h-3 w-3 text-gray-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500">Executed</span>
                      <div className="text-sm font-semibold text-gray-900">
                        {new Date(executedAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-red-50 p-4 text-center">
                <Search className="mx-auto mb-2 h-8 w-8 text-orange-400" />
                <div className="text-sm font-medium text-orange-700">
                  No keyword data to display
                </div>
                <div className="mt-1 text-xs text-orange-600">
                  The search query returned no results
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadSeoKeywords(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function seoKeywordsToMarkdown(nodeExecution: NodeExecution): string {
  // Skip schema validation - use CSV data directly like CSV Reader template tool
  const result = nodeExecution.outputs
  const resultData = Array.isArray(result) ? result[0] : result
  const keyword = resultData?.query?.keyword || ''
  const action = resultData?.query?.action || ''
  const database = resultData?.query?.database || ''

  return `## SEO Research - Keywords Analysis

**Keyword:** ${keyword}
**Action:** ${action}
**Database:** ${database}

### CSV Data
\`\`\`
${resultData?.csvData || 'No data available'}
\`\`\`

---
`
}

export const SeoKeywordsDisplay = createWorkflowToolComponent(
  SeoKeywordsDisplayComponent,
  downloadSeoKeywords,
  seoKeywordsToMarkdown
)
